import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const LOCAL_VERSION_KEY = "ilajly-app-version";
const LOCAL_BUILD_KEY = "ilajly-app-build-time";
const UPDATE_SUCCESS_KEY = "ilajly-update-success";
const CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes (was 5 — reduced API calls 3x)

interface VersionInfo {
  version: string;
  buildTime: string;
}

interface AppVersionState {
  currentVersion: string | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  updating: boolean;
  updateStage: string;
  updateProgress: number;
  updateSuccess: boolean;
  justUpdated: boolean;
  checkForUpdate: () => Promise<void>;
  performUpdate: () => Promise<void>;
  dismissUpdateSuccess: () => void;
}

export function useAppVersion(): AppVersionState {
  const [currentVersion, setCurrentVersion] = useState<string | null>(
    () => localStorage.getItem(LOCAL_VERSION_KEY)
  );
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestBuildTime, setLatestBuildTime] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateStage, setUpdateStage] = useState("");
  const [updateProgress, setUpdateProgress] = useState(0);
  const [justUpdated, setJustUpdated] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const checkingRef = useRef(false);

  useEffect(() => {
    const flag = localStorage.getItem(UPDATE_SUCCESS_KEY);
    if (flag) {
      setJustUpdated(true);
      localStorage.removeItem(UPDATE_SUCCESS_KEY);
    }
  }, []);

  const dismissUpdateSuccess = useCallback(() => {
    setJustUpdated(false);
  }, []);

  const fetchLatestVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  // Ask the active SW which deployed version it is paired with. If it lags
  // behind /version.json, the SW itself is stale and must be refreshed.
  const getActiveSWVersion = useCallback(async (): Promise<VersionInfo | null> => {
    if (!("serviceWorker" in navigator)) return null;
    const ctrl = navigator.serviceWorker.controller;
    if (!ctrl) return null;
    return await new Promise<VersionInfo | null>((resolve) => {
      const channel = new MessageChannel();
      const timer = setTimeout(() => resolve(null), 2500);
      channel.port1.onmessage = (ev) => {
        clearTimeout(timer);
        const v = ev.data?.version as VersionInfo | null | undefined;
        resolve(v ?? null);
      };
      try {
        ctrl.postMessage({ type: "GET_VERSION" }, [channel.port2]);
      } catch {
        clearTimeout(timer);
        resolve(null);
      }
    });
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const latest = await fetchLatestVersion();
      if (!latest) return;

      setLatestVersion(latest.version);
      setLatestBuildTime(latest.buildTime);

      const storedVersion = localStorage.getItem(LOCAL_VERSION_KEY);
      const storedBuild = localStorage.getItem(LOCAL_BUILD_KEY);

      if (!storedVersion) {
        localStorage.setItem(LOCAL_VERSION_KEY, latest.version);
        localStorage.setItem(LOCAL_BUILD_KEY, latest.buildTime);
        setCurrentVersion(latest.version);
        setUpdateAvailable(false);
      } else if (storedVersion !== latest.version || storedBuild !== latest.buildTime) {
        setUpdateAvailable(true);
      } else {
        // App-shell is current — also verify the active SW agrees, otherwise
        // the SW could be serving a stale precache that breaks ack/tick logic.
        try {
          const swVer = await getActiveSWVersion();
          if (swVer && (swVer.version !== latest.version || swVer.buildTime !== latest.buildTime)) {
            setUpdateAvailable(true);
          } else {
            setUpdateAvailable(false);
          }
        } catch {
          setUpdateAvailable(false);
        }
      }
    } finally {
      checkingRef.current = false;
    }
  }, [fetchLatestVersion, getActiveSWVersion]);

  const clearAllCaches = async () => {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  };

  const unregisterAndReRegisterSW = async () => {
    if (!("serviceWorker" in navigator)) return;
    const swTimeout = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
    try {
      const registrations = await Promise.race([
        navigator.serviceWorker.getRegistrations(),
        swTimeout(5000).then(() => [] as readonly ServiceWorkerRegistration[]),
      ]);
      for (const reg of registrations) {
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
          await swTimeout(500);
        }
        await Promise.race([reg.update(), swTimeout(5000)]);
      }
      // Tell the active SW to nuke runtime caches and broadcast a reload to
      // every open tab — keeps multi-tab clients in lockstep on a new build.
      try {
        navigator.serviceWorker.controller?.postMessage({ type: "FORCE_REFRESH" });
      } catch { /* noop */ }
    } catch {
      console.warn("Service worker update timed out, continuing...");
    }
  };


  const performUpdate = useCallback(async () => {
    setUpdating(true);
    setUpdateSuccess(false);
    try {
      setUpdateStage("Checking version…");
      setUpdateProgress(10);
      const latest = await fetchLatestVersion();
      if (!latest) {
        setUpdateStage("Could not reach server. Try again.");
        setUpdating(false);
        return;
      }
      await new Promise((r) => setTimeout(r, 400));

      setUpdateStage("Clearing cache…");
      setUpdateProgress(30);
      await clearAllCaches();
      await new Promise((r) => setTimeout(r, 400));

      setUpdateStage("Updating service worker…");
      setUpdateProgress(55);
      await unregisterAndReRegisterSW();
      await new Promise((r) => setTimeout(r, 400));

      setUpdateStage("Downloading update…");
      setUpdateProgress(80);
      localStorage.setItem(LOCAL_VERSION_KEY, latest.version);
      localStorage.setItem(LOCAL_BUILD_KEY, latest.buildTime);
      localStorage.setItem(UPDATE_SUCCESS_KEY, latest.version);
      await new Promise((r) => setTimeout(r, 600));

      setUpdateStage("Update complete!");
      setUpdateProgress(100);
      setUpdateSuccess(true);
      await new Promise((r) => setTimeout(r, 1500));
      window.location.reload();
    } catch (err) {
      console.error("Update failed:", err);
      setUpdateStage("Update failed. Please try again.");
      setUpdating(false);
      setUpdateSuccess(false);
    }
  }, [fetchLatestVersion]);

  // Check on mount and periodically — also use visibilitychange instead of blind polling
  useEffect(() => {
    checkForUpdate();
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL);

    // Also check when user returns to tab (event-driven > polling)
    const onVisible = () => {
      if (!document.hidden) checkForUpdate();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkForUpdate]);

  // Listen for SW lifecycle: controllerchange (new SW took over) and broadcast
  // messages from the SW itself (activated / forced reload).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onControllerChange = () => checkForUpdate();
    const onSWMessage = (ev: MessageEvent) => {
      const t = ev.data?.type;
      if (t === "SW_ACTIVATED") {
        // A new SW just claimed clients — re-check version so the banner clears
        // (or surfaces) without waiting for the next 15-min poll.
        void checkForUpdate();
      } else if (t === "SW_FORCE_RELOAD") {
        // Another tab triggered a forced refresh — reload to stay in lockstep.
        try { window.location.reload(); } catch { /* noop */ }
      }
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    navigator.serviceWorker.addEventListener("message", onSWMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      navigator.serviceWorker.removeEventListener("message", onSWMessage);
    };
  }, [checkForUpdate]);

  // On login: check for updates and auto-apply if available
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === "SIGNED_IN") {
        const latest = await fetchLatestVersion();
        if (!latest) return;
        const storedVersion = localStorage.getItem(LOCAL_VERSION_KEY);
        const storedBuild = localStorage.getItem(LOCAL_BUILD_KEY);
        if (storedVersion && (storedVersion !== latest.version || storedBuild !== latest.buildTime)) {
          performUpdate();
        } else if (!storedVersion) {
          localStorage.setItem(LOCAL_VERSION_KEY, latest.version);
          localStorage.setItem(LOCAL_BUILD_KEY, latest.buildTime);
          setCurrentVersion(latest.version);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [fetchLatestVersion, performUpdate]);

  return {
    currentVersion,
    latestVersion,
    updateAvailable,
    updating,
    updateStage,
    updateProgress,
    updateSuccess,
    justUpdated,
    checkForUpdate,
    performUpdate,
    dismissUpdateSuccess,
  };
}
